<template>
    <div class="lsva-calendar-filter">
        <div class="lsva-calendar-filter__calendar">
            <!-- Selected Day Display -->
            <div class="lsva-calendar-filter__header">
                <h1>{{ formattedSelectedDate }}</h1>
                <span class="t-subtle">
                    {{ $t('calendarWeek', [selectedWeek]) }}
                </span>
            </div>
            <slot name="center" />

            <!-- Date Navigation -->
            <div class="lsva-calendar-filter__days-container">
                <button
                    class="lsva-calendar-filter__nav-button"
                    :disabled="isCalendarLoading || isLeftDisabled"
                    @click="scrollDays(-1)"
                >
                    <ChevronLeftIcon />
                </button>
                <div class="lsva-calendar-filter__days-wrapper">
                    <div
                        v-if="isCalendarLoading"
                        class="l-padded-x l-inline l-center"
                    >
                        <VSpinner
                            size="medium"
                            :line-fg-color="spinnerColor"
                            :speed="1"
                        />
                    </div>
                    <div v-else class="lsva-calendar-filter__days">
                        <button
                            v-for="day in visibleDays"
                            :key="day.date"
                            class="lsva-calendar-filter__day"
                            :class="{
                                'lsva-calendar-filter__day--selected':
                                    day.date === selectedDay.date,
                                'lsva-calendar-filter__day--today':
                                    day.date === today,
                            }"
                            :disabled="day.date === selectedDay.date"
                            @click="handleDayClick(day.date)"
                        >
                            <div class="lsva-calendar-filter__day-label">
                                {{ day.dayFormatted }}
                            </div>
                            <div class="lsva-calendar-filter__day-number">
                                {{ day.dateFormatted }}
                            </div>
                            <div
                                v-if="day.hasMarker"
                                class="lsva-calendar-filter__day-marker"
                            />
                        </button>
                    </div>
                </div>
                <button
                    class="lsva-calendar-filter__nav-button"
                    :disabled="isCalendarLoading || isRightDisabled"
                    @click="scrollDays(1)"
                >
                    <ChevronRightIcon />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import moment from 'moment'
import VSpinner from 'vue-simple-spinner'

import ChevronLeftIcon from '../icons/ChevronLeftIcon.vue'
import ChevronRightIcon from '../icons/ChevronRightIcon.vue'

const MAX_VISIBLE_DAYS = 7 // Configurable visible days

export default {
    name: 'LsvaCalendarFilter',
    components: {
        ChevronLeftIcon,
        ChevronRightIcon,
        VSpinner,
    },
    data() {
        return {
            maxVisibleDays: window.innerWidth < 896 ? 4 : MAX_VISIBLE_DAYS, // show 4 days on mobile
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
            visibleStartIndex: 0, // Calculated dynamically
        }
    },
    computed: {
        ...mapState('lsva', ['isCalendarLoading', 'selectedDay']),
        ...mapGetters('lsva', ['preparedCalendarDays']),
        visibleDays() {
            return this.preparedCalendarDays.slice(
                this.visibleStartIndex,
                this.visibleStartIndex + this.maxVisibleDays
            )
        },
        isLeftDisabled() {
            return this.visibleStartIndex === 0
        },
        isRightDisabled() {
            return (
                this.visibleStartIndex + this.maxVisibleDays >=
                this.preparedCalendarDays.length
            )
        },
        formattedSelectedDate() {
            const selectedDate = moment(this.selectedDay.date)
            return selectedDate
                .format('LLLL')
                .replace(selectedDate.format('LT'), '')
                .replace(selectedDate.format('YYYY'), '')
                .replace(/,\s*$/, '')
                .trim()
        },
        selectedWeek() {
            return moment(this.selectedDay.date).isoWeek()
        },
        today() {
            return moment().format('YYYY-MM-DD')
        },
    },
    watch: {
        '$route.params.date': {
            handler(newDate) {
                this.setSelectedDayByDate(newDate)
            },
            immediate: true,
        },
    },
    async created() {
        moment.locale(this.$i18n.locale)

        await this.loadCalendarDays()

        const dateFromRoute = this.$route.params.date
        const lastDayDate = this.preparedCalendarDays[
            this.preparedCalendarDays.length - 1
        ].date

        this.visibleStartIndex = Math.max(
            this.preparedCalendarDays.length - this.maxVisibleDays,
            0
        )

        if (dateFromRoute) {
            this.setSelectedDayByDate(dateFromRoute)
        } else {
            this.handleDayClick(lastDayDate)
        }
    },
    methods: {
        ...mapActions('lsva', [
            'loadCalendarDays',
            'setSelectedDay',
            'setSelectedDayByDate',
        ]),
        handleDayClick(date) {
            const newQuery = { ...this.$route.query }
            delete newQuery.selected_declaration

            this.$router.push({
                name: 'lsvaDeclarations',
                params: { date },
                query: newQuery,
            })
        },
        scrollDays(direction) {
            this.visibleStartIndex = Math.min(
                Math.max(this.visibleStartIndex + direction, 0),
                this.preparedCalendarDays.length - this.maxVisibleDays
            )
        },
    },
}
</script>

<i18n>
{
    "en": {
        "calendarWeek": "Calendar week {0}"
    },
    "de": {
        "calendarWeek": "Kalenderwoche {0}"
    },
    "fr": {
        "calendarWeek": "Semaine Civile {0}"
    },
    "it": {
        "calendarWeek": "Settimana di Calendario {0}"
    }
}
</i18n>

<style lang="scss" scoped>
.lsva-calendar-filter {
    width: 100%;

    &__calendar {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 1rem;

        @include respond-to('for-mobile-down') {
            flex-direction: column;
        }
    }

    &__header {
        h1 {
            font-size: 28px;
            color: $color-primary;
        }
    }

    &__days-container {
        display: flex;
        align-items: center;
        justify-content: center;
        max-width: 100%;
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.08);
        overflow: hidden;
    }

    &__days-wrapper {
        overflow: hidden;
        width: 100%;
        height: 100%;

        & > * {
            height: 100%;
        }

        @include respond-to('for-mobile-down') {
            overflow-x: auto;
        }
    }

    &__days {
        display: flex;
        height: 100%;
    }

    &__day {
        flex: 1;
        position: relative;
        width: 70px;
        padding: 8px;
        border: none;
        background-color: #fff;
        font-family: 'IBM Plex Sans', sans-serif;
        font-size: 16px;
        color: $color-primary;
        text-align: center;
        transition: background 0.1s;
        white-space: nowrap;
        cursor: pointer;

        &:not(:disabled) {
            &:hover {
                background-color: darken(#fff, 1%);
            }
        }

        &--today {
            color: $color-blue-light;
        }

        &--selected {
            background-color: $color-primary;
            color: $color-primary-contrast;
        }

        &-label {
            font-weight: bold;
        }

        &-number {
            font-size: $fs-small;
        }

        &-marker {
            width: 6px;
            height: 6px;
            background-color: $color-orange;
            border-radius: 50%;
            position: absolute;
            top: 5px;
            right: 5px;
        }
    }

    &__nav-button {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-height: 54px;
        padding: 0.5rem;
        background-color: #fff;
        border: none;
        color: $color-primary;
        transition: all 0.1s;
        cursor: pointer;

        &:not(:disabled) {
            &:hover {
                background-color: darken(#fff, 1%);
            }
        }

        &:disabled {
            color: $color-primary-lighter;
            cursor: not-allowed;
        }

        &:first-of-type {
            border-right: 1px solid rgba(0, 0, 0, 0.1);
        }

        &:last-of-type {
            border-left: 1px solid rgba(0, 0, 0, 0.1);
        }
    }
}
</style>
